import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import CFInput from "./CFInput";

const CFImageUpload = ({
  name,
  value: initialValue = "",
  websiteId,
  environment,
  uploadPath,
  previewMaxWidth = "200px",
  previewMaxHeight = "auto",
  onChange,
  onBlur,
  onRemove,
  showInput = false,
  showRemoveButton = true,
  note, // Optional note message below
  darkPreview = false, // Show preview on dark background
  fullWidthButton = false, // Make button full width
  className = "",
  buttonClassName = "",
  previewClassName = "",
}) => {
  const [value, setValue] = useState(initialValue);
  const [timeoutId, setTimeoutId] = useState(null);
  const imagePreviewRef = useRef(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    
    // Clear existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Set new timeout for delayed update
    const newTimeoutId = setTimeout(() => {
      if (onChange) {
        onChange(newValue);
      }
    }, 500);
    
    setTimeoutId(newTimeoutId);
  };

  const handleUploadClick = () => {
    if (!websiteId || !environment) {
      console.error("websiteId and environment are required for uploads");
      return;
    }

    uploads.select("callback", {
      environment: environment,
      path: uploadPath || `/websites/${websiteId}/logos/`,
      website_id: websiteId,
      library: false,
      callback: function (file_url) {
        setValue(file_url);
        if (onBlur) {
          onBlur(file_url);
        } else if (onChange) {
          onChange(file_url);
        }
      },
    });
  };

  const handleRemove = () => {
    setValue("");
    if (onBlur) {
      onBlur("");
    } else if (onChange) {
      onChange("");
    }
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <div className={`form-inputs ${className}`}>
      {/* Preview Area with Border */}
      {value && (
        <div 
          className={`w-full border rounded-lg p-6 mb-4 text-center ${darkPreview ? 'bg-slate-800 border-slate-600' : 'bg-gray-50 border-gray-200'} ${previewClassName}`}
          style={{ 
            minHeight: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative"
          }}
        >
          <img
            ref={imagePreviewRef}
            src={value}
            style={{ 
              maxWidth: previewMaxWidth, 
              maxHeight: previewMaxHeight 
            }}
            alt="Preview"
          />
          {showRemoveButton && (
            <div
              onClick={handleRemove}
              style={{ 
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
                backgroundColor: "transparent",
                borderRadius: "4px",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e5e7eb"
              }}
              title="Reset"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4.87115 14.5C5.88914 17.6939 8.80463 20 12.2424 20C16.5268 20 20 16.4183 20 12C20 7.58172 16.5268 4 12.2424 4C9.37103 4 6.86399 5.60879 5.52267 8M7.87879 9H4V5"
                  stroke={darkPreview ? "#ffffff" : "#374151"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
      )}

      <CFInput
        type={showInput ? "text" : "hidden"}
        name={name}
        value={value || ""}
        className="mb-5"
        placeholder="https://example.com/brand_logo.jpg"
        onBlur={(value) => {
          if (onBlur) {
            onBlur(value);
          } else if (onChange) {
            onChange(value);
          }
        }}
      />

      {/* Upload Button */}
      <button
        type="button"
        onClick={handleUploadClick}
        className={`btn py-3 px-6 bg-black text-white hover:!text-white border-none rounded-lg font-medium ${buttonClassName}`}
        style={{ 
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: fullWidthButton ? "100%" : "auto"
        }}
      >
        <span 
          className="glyphicon glyphicon-cloud-upload" 
          style={{ fontSize: "14px" }} 
          aria-hidden="true"
        />
        Upload Image
      </button>
      
      {/* Note Message */}
      {note && (
        <div 
          className="mt-3 text-sm text-slate-500"
        >
          {note}
        </div>
      )}
    </div>
  );
};

CFImageUpload.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  websiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  environment: PropTypes.string.isRequired,
  uploadPath: PropTypes.string,
  previewMaxWidth: PropTypes.string,
  previewMaxHeight: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onRemove: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  showInput: PropTypes.bool,
  note: PropTypes.string,
  darkPreview: PropTypes.bool,
  fullWidthButton: PropTypes.bool,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  previewClassName: PropTypes.string,
};

export default CFImageUpload;
